import { gql, useQuery } from '@apollo/client';
import { useUserWithCompany } from '@app/hooks/useUserWithCompany';
import { URGENT_REQUEST_STATUSES } from '@dieterApi/rightsRequest/useRightsRequestsQuery';

// 72h reporting deadline status of a data breach, computed by dieter-server
// (src/utils/incidentDeadline.ts) per Art. 33 Abs. 1 DSGVO.
// NOT_REQUIRED: the questionnaire determined no authority report is needed (or has
// not reached that determination yet) - no deadline runs.
export type IncidentDeadlineStatus =
  | 'NOT_REQUIRED'
  | 'OPEN'
  | 'DUE_SOON'
  | 'CRITICAL'
  | 'OVERDUE'
  | 'DONE'
  | 'UNKNOWN';

export interface IncidentDeadline {
  index: string;
  description?: string;
  occurredAt?: string;
  discoveredAt?: string;
  deadline?: string;
  hoursRemaining?: number;
  status: IncidentDeadlineStatus;
  reportRequired: boolean;
  reportedAt?: string;
  questionnaireId?: string;
}

export function isUrgentIncident(incident: { status: IncidentDeadlineStatus }): boolean {
  return (URGENT_REQUEST_STATUSES as string[]).includes(incident.status);
}

export const GET_INCIDENT_DEADLINES = gql`
  query GetIncidentDeadlines($companyId: String!) {
    getIncidentDeadlines(companyId: $companyId) {
      index
      description
      occurredAt
      discoveredAt
      deadline
      hoursRemaining
      status
      reportRequired
      reportedAt
      questionnaireId
    }
  }
`;

export interface GetIncidentDeadlinesResult {
  getIncidentDeadlines: IncidentDeadline[];
}

export interface GetIncidentDeadlinesInput {
  companyId: string;
}

export function useIncidentDeadlinesQuery() {
  const { user } = useUserWithCompany();
  const companyId = user?.company?.id;

  return useQuery<GetIncidentDeadlinesResult, GetIncidentDeadlinesInput>(GET_INCIDENT_DEADLINES, {
    variables: { companyId: companyId || '' },
    skip: !companyId,
    // The 72h scale moves within a session - re-sync whenever a consumer mounts
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
}
